import MapView from '@arcgis/core/views/MapView';
import { DeckGLLayer } from '@deck.gl/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import {
  LayerState, LayerRemoveFunction, LayerAddFunction, debugLog,
} from './utils/layer-change-coordinator';
import StandardLayerConverter from './utils/standard-layer-converter';
import { Layer } from '../../../models/layer-settings';
import { MapPlugin } from '../arcgis-types';

/**
 * The StandardLayers plugin is responsible for rendering most layer types,
 * especially if they can easily be mapped to a cooresponding ArcGIS Layer.
 *
 */
const StandardLayers = (
  layerState: LayerState,
  apiToken: string,
): MapPlugin => {
  const STANDARD_LAYER_NAME_PREFIX = 'FULCRUM_STANDARD_LAYER_';

  const initLayers = (mapView: MapView) => {
    const arcLayers: { index: number, arcLayer: DeckGLLayer | FeatureLayer, layer: Layer }[] = [];
    const activeOverlays = layerState.activeOverlays();

    let i = 0;
    [...activeOverlays].reverse().forEach((layer) => {
      const arcLayer = StandardLayerConverter(layer, apiToken);

      if (arcLayer && !(arcLayer instanceof Promise)) {
        arcLayer.id = `${STANDARD_LAYER_NAME_PREFIX}${layer.id}`;
        arcLayers.push({ index: i, arcLayer, layer });
      }

      i++;
    });

    arcLayers.forEach((loadableLayer) => {
      const { index, arcLayer, layer } = loadableLayer;
      debugLog('Initing standard layer', index, layer);
      mapView.map.add(arcLayer, index);
    });
  };

  return {
    extendMap: (mapView: MapView) => {
      initLayers(mapView);

      const removeLayer: LayerRemoveFunction = (layer) => {
        const arcLayer = mapView.map?.findLayerById(`${STANDARD_LAYER_NAME_PREFIX}${layer.id}`);
        if (arcLayer) {
          debugLog('Removing standard layer', layer.id, layer);
          mapView.map.remove(arcLayer);
        }
      };

      const addLayer: LayerAddFunction = (index, layer) => {
        const arcLayer = StandardLayerConverter(layer, apiToken);
        if (arcLayer && !(arcLayer instanceof Promise)) {
          arcLayer.id = `${STANDARD_LAYER_NAME_PREFIX}${layer.id}`;
          debugLog('Adding standard layer', index, layer);
          mapView.map?.add(arcLayer, index);
        }
      };

      layerState.onLayerChange({ remove: removeLayer, add: addLayer });
    },
  };
};

export default StandardLayers;
